import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UseApi } from '../Hooks/UseApi';
import { 
  Alert, 
  Box, 
  Typography, 
  CircularProgress,
  Snackbar,
  Slide
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

const StudioSigninHandler = () => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const api = UseApi();
  const apiRef = useRef(null);
  
  // Keep apiRef updated with latest api
  apiRef.current = api;
  
  // State management
  const [fixStatus, setFixStatus] = useState(null);
  const [isFixing, setIsFixing] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // CRITICAL: Use refs to prevent multiple executions
  const hasRunForUser = useRef(new Set());
  const isCurrentlyProcessing = useRef(false);
  const lastProcessedUser = useRef(null);

  // OPTIMIZATION: Only run once per user session
  useEffect(() => {
    if (!isAuthenticated || isLoading || !user?.email || 
        isCurrentlyProcessing.current || 
        hasRunForUser.current.has(user.email)) {
      return;
    }

    const shouldDelay = !lastProcessedUser.current;
    const delay = shouldDelay ? 1500 : 100;

    const timer = setTimeout(() => {
      handleStudioAutoFix();
    }, delay);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, user?.email]);

  // RESET: Clear tracking when user changes
  useEffect(() => {
    if (user?.email && lastProcessedUser.current && lastProcessedUser.current !== user.email) {
      hasRunForUser.current.clear();
      isCurrentlyProcessing.current = false;
    }
  }, [user?.email]);

  const handleStudioAutoFix = async () => {
    if (isCurrentlyProcessing.current || !user?.email) {
      return;
    }

    isCurrentlyProcessing.current = true;
    hasRunForUser.current.add(user.email);
    lastProcessedUser.current = user.email;

    try {
      setIsFixing(true);
      let appliedFixes = [];
      let statusMessage = '';

      try {
        const nameAssignResponse = await apiRef.current.fetchWithAuth('/studio/auto-assign-name/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (nameAssignResponse.studio_assigned) {
          appliedFixes.push('Auto-assigned studio name to your profile');
          statusMessage = `Welcome to ${nameAssignResponse.studio_name}!`;
        }
      } catch (nameError) {
        // This is optional, don't fail the whole process
      }

      if (appliedFixes.length > 0) {
        setFixStatus({
          type: 'success',
          message: statusMessage,
          details: appliedFixes,
          needsRefresh: false
        });

        setSnackbarMessage(`Studio setup completed! ${appliedFixes.length} updates applied.`);
        setSnackbarSeverity('success');
        setShowSnackbar(true);

        setTimeout(() => {
          setFixStatus(null);
        }, 5000);
      }

    } catch (error) {
      console.error('Error in studio setup:', error);
      
      if (error.message.includes('500') || error.message.includes('Network')) {
        setSnackbarMessage('Studio setup encountered an issue. Please refresh if you experience access problems.');
        setSnackbarSeverity('warning');
        setShowSnackbar(true);
      }
      
    } finally {
      setIsFixing(false);
      isCurrentlyProcessing.current = false;
    }
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  const handleCloseStatus = () => {
    setFixStatus(null);
  };

  // Show prominent status if major fixes were applied
  if (fixStatus) {
    return (
      <Box sx={{ mb: 2, position: 'relative', zIndex: 1000 }}>
        <Alert 
          severity={fixStatus.type} 
          onClose={handleCloseStatus}
          sx={{ 
            mb: 1,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
          icon={<CheckCircle />}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {fixStatus.message}
          </Typography>
          
          {fixStatus.details && fixStatus.details.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Updates applied:
              </Typography>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                {fixStatus.details.map((detail, index) => (
                  <li key={index} style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                    {detail}
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  // Show subtle loading indicator only if actively fixing
  if (isFixing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 1, 
        p: 1.5, 
        bgcolor: 'background.paper', 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        opacity: 0.8
      }}>
        <CircularProgress size={18} sx={{ mr: 1.5 }} />
        <Typography variant="body2" color="textSecondary">
          Setting up studio access...
        </Typography>
      </Box>
    );
  }

  // Snackbar for subtle notifications
  return (
    <Snackbar
      open={showSnackbar}
      autoHideDuration={4000}
      onClose={handleCloseSnackbar}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'down' }}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        onClose={handleCloseSnackbar} 
        severity={snackbarSeverity}
        sx={{ width: '100%' }}
        variant="filled"
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  );
};

export default StudioSigninHandler;